import React, { useState, useEffect, useMemo } from 'react';
import {
	Box,
	Grid,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Tooltip,
	IconButton,
	Stack,
	Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import PageHeading from '../../Components/PageHeading';
import SearchBar from '../../Components/SearchBar';
import PageLoader from '../../Components/PageLoader';
import CustomTablePagination from '../../Components/CustomTablePagination';
import DataNotFound from '../../Components/DataNotFound';
import { getColorSettings, getFormattedDuration } from '../../Utils/helper/commonUtils';
import LineGraphCard from '../../Screens/Dashboard/Components/LineGraphCard';
import { Modal } from '@mui/material';
import CultService from '../../Services/CultService';
import { formatDate } from '../../Utils/helper/commonUtils';
import { AppColors } from '../../Utils/helper/AppColors';
import UserThumbnail from '../../Components/User/UserThumbnail';
import CustomTag from '../../Components/CustomTag';
import moment from 'moment';
import dayjs from 'dayjs';
import PlayingModal from '../Recordings/Components/PlayingModal';
import { PlayCircleIcon } from '../../Components/Icons/Icons';
import QrCodeModal from '../../Components/QrCodeModal';
import { red } from '@mui/material/colors';
import { Filter } from '../Campaign/Components/CampaignSteps/CampaignUsers';
import toast from 'react-hot-toast';
import useResponsive from '../../Utils/hooks/useResponsive';

export default function InboundCallList() {
	const [list, setList] = useState([]);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [pagination, setPagination] = useState({ count: 0, rowsPerPage: 10, page: 1 });
	const company = useSelector((state) => state?.app?.company);
	const [modalOpen, setModalOpen] = useState(false);
	const [statsModalOpen, setStatsModalOpen] = useState(false);
	const [incomingCallStats, setIncomingCallStats] = useState([]);
	const [statsDateRange, setStatsDateRange] = useState({
		from: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
		to: dayjs().format('YYYY-MM-DD'),
	});

	const [filterQuery, setFilterQuery] = useState({});
	const [selected, setSelected] = useState(null);

	console.log('filterQuery:', filterQuery);

	const getList = async (page = 1, limit = pagination?.rowsPerPage, searchTerm = search || '') => {
		if (!company?._id) return;

		setLoading(true);
		try {
			const response = await CultService.getIncomingCallListPaginated({
				data: {
					// companyId: company?._id,
					page: page,
					limit: limit,
					search: searchTerm,
					filters: {
						...filterQuery,
					},
				},
			});
			console.log('Inbound Call List Response:', response);
			if (response?.data?.success) {
				setList(response?.data?.data?.incomingCalls || []);
				setPagination({
					...pagination,
					count: response?.data?.data?.pagination?.total || 0,
					page : response?.data?.data?.pagination?.page || 1,
					rowsPerPage: response?.data?.data?.pagination?.limit || 10,
				});
			}
		} catch (error) {
			console.error('Error fetching inbound call list:', error);
			toast.error('Failed to fetch inbound call list');
		} finally {
			setLoading(false);
		}
	};

	const debounceGetList = debounce((searchTerm, paginationData) => {
		getList(paginationData.page, paginationData.rowsPerPage, searchTerm);
	}, 1000);

	const fetchIncomingCallStats = async ({ from, to, groupBy = 0 }) => {
		try {
			let res = await CultService.getIncomingCallStatistics({
				params: { from, to, groupBy },
			});
			if (res?.status === 200) {
				setIncomingCallStats(res?.data?.data?.chartData || []);
				console.log('Incoming Call Statistics Response:', res);
			}
		} catch (err) {
			console.error('Error fetching incoming call statistics:', err);
		}
	};

	// Run when search term changes
	useEffect(() => {
		if (company?._id) {
			debounceGetList(search, pagination);
		}
	}, [company, search , filterQuery]);

	// Fetch statistics when date range changes
	useEffect(() => {
		if (statsModalOpen) {
			fetchIncomingCallStats(statsDateRange);
		}
	}, [statsDateRange, statsModalOpen]);

	// Prepare data for Statistics LineGraphCard
	const incomingCallGraphValues = useMemo(
		() =>
			incomingCallStats.map((item) => ({
				name: dayjs(item.date).format('DD MMM'),
				value: item.count,
			})),
		[incomingCallStats],
	);

	// Table columns
	const columns = [
		{ id: 'name', label: 'Name', width: '20%', minWidth: 150, maxWidth: 200 },
		{ id: 'department', label: 'Department', width: '15%', minWidth: 120, maxWidth: 180 },
		{ id: 'dateTime', label: 'Date/Time', width: '16%', minWidth: 150, maxWidth: 200 },
		{ id: 'summary', label: 'Summary', width: '36%', minWidth: 360, maxWidth: 400 },
		{ id: 'duration', label: 'Duration', width: '8%', minWidth: 100, maxWidth: 150 },
		{ id: 'recording', label: 'Recording', width: '5%', minWidth: 80, maxWidth: 120 },
	];
	const localSettingColors = getColorSettings();
	const {small , medium} = useResponsive();

	return (
		<Stack sx={{ padding: 2 }} gap={3}>
			<Grid item xs={12}>
				<PageHeading title="Inbound Calls" subTitle="Detailed overview of inbound calls" />
			</Grid>

			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap' }} gap={2}>
				<Stack direction="row" alignItems="center" gap={1}>
					<SearchBar
						placeholder="Search by email or phone number..."
						search={search}
						color={AppColors.midnight_black || localSettingColors.fontColor}
						bgColor={AppColors.lightest_grey || localSettingColors.quinary}
						setSearch={setSearch}
						width={small ? '300px' : medium ? '450px' : '702px'}
					/>
					<Filter
						filterQuery={filterQuery}
						setFilterQuery={setFilterQuery}
						filterTypes={['genders', 'roles', 'timezones', 'departments']}
						buttonSize="medium"
					/>
				</Stack>

				<Stack direction="row" gap={2}>
					<Button
						variant="outlined"
						color="info"
						onClick={() => {
							setModalOpen(true);
						}}
					>
						Generate QR
					</Button>
					<Button
						variant="contained"
						color="info"
						onClick={() => {
							setStatsModalOpen(true);
							fetchIncomingCallStats(statsDateRange);
						}}
					>
						View Statistics
					</Button>
				</Stack>
			</Stack>

			<Grid item xs={12} mt={1}>
				{loading ? (
					<PageLoader />
				) : (
					<TableContainer sx={{ height: 'calc(100vh - 200px)', backgroundColor: AppColors.WHITE }}>
						{list.length === 0 ? (
							<DataNotFound message="No inbound calls found" />
						) : (
							<Table stickyHeader>
								<TableHead
									sx={{
										'& .MuiTableCell-root': {
											fontSize: 14,
											lineHeight: '150%',
											letterSpacing: '0%',
											fontFamily: 'Poppins',
											color: AppColors.HEADING_TITLE2,
											fontWeight: 600,
											paddingTop: '16px',
											paddingBottom: '20px',
										},
									}}
								>
									<TableRow>
										{columns.map((column) => (
											<TableCell
												key={column.id}
												align="left"
												style={{
													minWidth: column.minWidth,
													maxWidth: column.maxWidth,
													width: column.width,
												}}
											>
												{column.label}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{list.map((row, index) => (
										<TableRow
											sx={{
												'& .MuiTableCell-root': {
													paddingTop: '10px',
													paddingBottom: '10px',
												},
											}}
											key={index}
										>
											<TableCell>
												<Stack direction="column" alignItems="start">
													<UserThumbnail
														noPagination={true}
														userId={row?.anonymous ? null : row?.user?._id}
														user={row?.anonymous ? null : row?.user}
													/>
													<Typography sx={{ fontSize: 14, fontWeight: 400, color: AppColors.HEADING_TITLE4 }}>
														{!row?.anonymous && row?.user?.phoneNumber}
													</Typography>
													<Typography
														sx={{ fontSize: 14, fontWeight: 400, color: AppColors.HEADING_TITLE4, whiteSpace: 'nowrap' }}
													>
														{!row?.anonymous && row?.user?.email}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												{row?.user?.department &&  (
													<CustomTag tagTitle={row?.user?.department} fontSize={14} fontWeight={400} lineHeight={'150%'} colorsInfo={{ "color": AppColors.midnight_black, "bgColor": AppColors.lightest_grey, "border": AppColors.light_grey }} />
												)}
											</TableCell>
											<TableCell>
												{row?.callDataFetched && (
													<Stack direction="column" alignItems="start">
														<Typography sx={{ fontSize: 14, fontWeight: 600, color: AppColors.HEADING_TITLE4 }}>
															{row?.call?.startedAt && moment(row?.call?.startedAt).format('MM-DD-YYYY')}
														</Typography>
														<Typography sx={{ fontSize: 14, fontWeight: 600, color: AppColors.HEADING_TITLE4 }}>
															{row?.call?.startedAt && moment(row?.call?.startedAt).format('hh:mm A')}
														</Typography>
													</Stack>
												)}
											</TableCell>
											<TableCell>
												<Tooltip title={ row?.callSummary?.summary} arrow>
													<Typography
														sx={{
															fontSize: 14,
															fontWeight: 400,
															color: AppColors.HEADING_TITLE4,
															display: '-webkit-box',
															WebkitBoxOrient: 'vertical',
															WebkitLineClamp: 3, // Limit to 3 lines
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{ row?.callSummary?.summary}
													</Typography>
												</Tooltip>
											</TableCell>
											<TableCell>
												{row?.callDataFetched && 
													getFormattedDuration(row?.call?.startedAt, row?.call?.endedAt, 'mm:ss')}
											</TableCell>

											<TableCell>
												{row?.callDataFetched && !!row?.call && (
													<Stack
														direction={'row'}
														alignItems={'center'}
														spacing={'4px'}
														onClick={() => setSelected(row)}
														sx={{
															pointerEvents: row?.anonymous  ? 'none' : 'auto',
															opacity: row?.anonymous  ? 0.2 : 1,
															cursor: 'pointer',
														}}
													>
														<PlayCircleIcon color={localSettingColors.fontColor} size={26} />
														<Typography
															fontSize={14}
															fontWeight={600}
															lineHeight={'150%'}
															sx={(theme) => ({
																color: theme.palette.primary.darker_primary,
															})}
															whiteSpace="nowrap"
														>
															Recording {index + 1}
														</Typography>
													</Stack>
												)}
											</TableCell>
										</TableRow>
									))}

									<TableRow
										sx={{
											position: 'sticky',
											bottom: 0,
											backgroundColor: '#fff',
											zIndex: 2,
										}}
									>
										<TableCell sx={{ paddingY: '4px' }} colSpan={12} align="center">
											<CustomTablePagination
												{...pagination}
												onChange={(p, r) => getList(p, r)}
												onRowsPerPageChange={(e) => getList(1, e.target.value)}
											/>
											{/* <CustomTablePagination
												count={pagination.count}
												rowsPerPage={pagination.rowsPerPage}
												page={pagination.page}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
											/> */}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						)}
					</TableContainer>
				)}
			</Grid>
			<PlayingModal open={Boolean(selected)} onClose={() => setSelected(null)} selected={selected} />
			<QrCodeModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				companyId={company?._id}
				company={company}
				scannerComponent={<div>QR Scanner Placeholder</div>} // Replace with your scanner component
			/>

			{/* Call Statistics Modal */}
			<Modal
				open={statsModalOpen}
				onClose={() => setStatsModalOpen(false)}
				aria-labelledby="call-statistics-modal"
				aria-describedby="modal-displaying-call-statistics"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '70%',
						maxWidth: 1000,
						bgcolor: 'background.paper',
						borderRadius: '10px',
						boxShadow: 24,
						zIndex: 1400, // Higher than backdrop
					}}
				>
					<LineGraphCard
						value={null}
						values={incomingCallGraphValues}
						title="Incoming Call Volume"
						showFilter
						xAxisSpace
						color={localSettingColors.tertiary}
						onDateRangeChange={setStatsDateRange}
						tooltipTitle={'Statistics showing the volume of incoming calls over the selected time period.'}
						placement="right-start"
					/>
				</Box>
			</Modal>
		</Stack>
	);
}
